import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import http from '@/utils/http.service';
import { agendaSlotReservationSchema } from '../queries/use-get-office-agendas-slots';

const confirmAgendaSlotReservationResponseSchema = agendaSlotReservationSchema;

export type ConfirmAgendaSlotReservationResponse = z.infer<
  typeof confirmAgendaSlotReservationResponseSchema
> & {
  autoDeclinedIds: string[];
};

const confirmAgendaSlotReservation = ({
  id,
  tag,
}: {
  id: string;
  tag?: string;
}) => {
  return http.agendaSlotReservations
    .confirmById(id, { tag })
    .then<ConfirmAgendaSlotReservationResponse>(({ response, transform }) => {
      const result = transform.handle({
        response,
        attributes: {
          status: true,
          tag: true,
        },
        relations: {
          doctorSubstitute: true,
        },
        schema: confirmAgendaSlotReservationResponseSchema,
      });

      return {
        ...result,
        doctorSubstitute: result.doctorSubstitute!,
        autoDeclinedIds: response.meta?.autoDeclinedIds ?? [],
      };
    });
};

const useConfirmAgendaSlotReservationMutation = (): UseMutationResult<
  ConfirmAgendaSlotReservationResponse,
  Error,
  { id: string; tag?: string }
> => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: confirmAgendaSlotReservation,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['office-agenda-slots'] });
      toast.success(t('http.confirm-agenda-slot-reservation.success'));
    },
  });
};

export default useConfirmAgendaSlotReservationMutation;
